import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { Upload, ImageIcon, Loader2, Sparkles, X, Crown, Brain } from "lucide-react";
import { addDoc, collection, serverTimestamp, updateDoc, doc, increment } from "firebase/firestore";

// 引用新架構
import { db } from "../config/firebase"; 
import { appId, API_URL } from "../config/constants";
import { compressThumbnail, safeReadWorkerText } from "../utils/helpers";
import SmartNav from "../components/common/SmartNav";
import ProcessingModal from "../components/common/ProcessingModal";
import CopyrightFooter from "../components/common/CopyrightFooter";

const InputPage = ({ user, userData, onCheckQuota, plansMap, sysConfig }) => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("math");
  const [inputText, setInputText] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState("text");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // 🗓️ 核心邏輯：判斷「語文教室」是否開放
  const isLiberalArtsOpen = () => {
      // 1. 鑽石會員永遠開放
      if ((userData?.planId || "").toUpperCase() === "C") return true;
      // 2. 個人權限
      if (userData?.allowDualMode === true) return true;
      if (!sysConfig) return false;
      // 3. 全域開放
      if (sysConfig.enableFullSubjects === true) return true;
      // 4. 排程開放
      const now = new Date().getTime();
      const start = sysConfig.eventStartDate ? new Date(sysConfig.eventStartDate).getTime() : null;
      const end = sysConfig.eventEndDate ? new Date(sysConfig.eventEndDate).getTime() : null;
      if (start && now < start) return false;
      if (end && now > end) return false;
      if (start || end) return true;
      return false;
  };

  const showFullSubjectTabs = isLiberalArtsOpen();

  useEffect(() => {
    if (!showFullSubjectTabs && subject === 'full') {
      setSubject("math");
    }
  }, [showFullSubjectTabs, subject]);

  useEffect(() => {
    setInputText("");
    setImageBase64(null);
    setSelectedImage(null);
  }, []);

  const processFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;
        let scale = 1;
        if (img.width > MAX_WIDTH) scale = MAX_WIDTH / img.width;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setImageBase64(canvas.toDataURL("image/jpeg", 0.6));
      };
    };
    reader.readAsDataURL(file);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) { processFile(items[i].getAsFile()); break; }
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]); };

  const executeSolve = async (isPaid = false) => {
    if (userData?.lastSolvedAt) {
        const lastTime = userData.lastSolvedAt.toDate().getTime();
        const now = new Date().getTime();
        const diff = (now - lastTime) / 1000;
        const cooldown = 60;
        if (diff < cooldown) {
            alert(`⏳ 大腦冷卻中！請先吸收一下剛才的內容。\n\n請等待 ${Math.ceil(cooldown - diff)} 秒後再問下一題。`);
            return;
        }
    }
    
    if (!inputText && !imageBase64) return;
    setLoadingType(imageBase64 ? "image" : "text");
    setLoading(true);

    try {
      const cleanImage = imageBase64 ? imageBase64.split(',')[1] : null;
      const visionPrompt = "\n\n【視覺指令】若圖片包含幾何圖形或圖表，請務必將「圖形特徵」(如：三角形ABC、座標點、角度、邊長數據) 自動轉化為「詳細的文字敘述」並整合在題目中。";
      const finalPrompt = (inputText || "請詳細解題") + visionPrompt;

      const r = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: finalPrompt, 
          imageBase64: cleanImage,
          action: "solve",
          subjectType: subject
        })
      });

      if (!r.ok) throw new Error("Worker 連線錯誤");

      const { text, data } = await safeReadWorkerText(r);

      if (data?.refusal || text.includes("[REFUSAL]")) {
          let alertMsg = "⚠️ 跑錯教室囉！\n\n這題似乎不屬於目前的科目頻道。\n系統已自動攔截請求。";
          if (isPaid) {
              const cost = sysConfig?.pointCostQuestion || 5;
              const userRef = doc(db, "artifacts", appId, "public", "data", "registered_users", userData.email.toLowerCase());
              await updateDoc(userRef, { points: increment(cost) });
              alertMsg += `\n\n💰 已自動退還您扣除的 ${cost} 點數！`;
          }
          alert(alertMsg);
          setLoading(false);
          return;
      }

      if (!text || text.length < 5) throw new Error("AI 無法辨識內容");

      let simple = "";
      let detailed = text;
      const splitRegex = /\[詳細過程\]|\[詳細解析\]/;
      const simpleMarker = "[簡易解答]";
      if (text.includes(simpleMarker)) {
          const parts = text.split(splitRegex);
          if (parts.length > 1) {
              simple = parts[0].replace(simpleMarker, "").trim();
              detailed = parts[1].trim();
          } else {
              simple = text.replace(simpleMarker, "").trim();
          }
      } else {
          simple = text.substring(0, 50) + "...";
      }

      let realLogId = "temp_pending";
      if (userData && user) {
        try {
          let thumbUrl = null;
          if (imageBase64) thumbUrl = await compressThumbnail(imageBase64);

          const docRef = await addDoc(collection(db, "artifacts", appId, "public", "data", "question_logs"), {
            userId: user.uid,
            userName: userData.name,
            userEmail: userData.email,
            questionText: inputText || "(圖片題)",
            timestamp: serverTimestamp(),
            aiResponse: simple,
            fullResponse: detailed,
            hasImage: !!imageBase64,
            quizCount: 0,
            hasUsedConcept: false,
            type: "normal",
            thumbnail: thumbUrl || null,
            subjectType: subject 
          });

          realLogId = docRef.id;
          const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Taipei" });
          const isNewDay = userData.quotaDate !== todayKey;

          await updateDoc(
            doc(db, "artifacts", appId, "public", "data", "registered_users", userData.email.toLowerCase()),
            {
              ...(isNewDay ? { quotaDate: todayKey, todaySolved: 0, todayQuizzes: 0, todayConcepts: 0 } : {}),
              totalQuestions: increment(1),
              todaySolved: isNewDay ? 1 : increment(1),
              lastActiveAt: serverTimestamp(),
            }
          );
        } catch (err) {
          console.error("存檔失敗:", err);
        }
      }

      navigate("/result", {
        state: {
          simple, detailed,
          question: inputText || "(圖片題)",
          imageBase64: imageBase64,
          logId: realLogId,
          subjectType: subject 
        }
      });
    } catch (e) {
      console.error(e);
      alert("解題失敗：" + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSolve = () => {
      onCheckQuota('question', (isPaid) => executeSolve(isPaid));
  };

  const isDiamond = userData?.planId === 'C';
  const isMath = subject === 'math';
  const activeColorClass = isMath ? "text-orange-600 bg-white shadow-md ring-1 ring-orange-100" : "text-emerald-600 bg-white shadow-md ring-1 ring-emerald-100";
  const inactiveColorClass = "text-slate-400 hover:text-slate-600 hover:bg-slate-200/50";
  const borderColorClass = isDragging ? "border-orange-400 ring-4 ring-orange-100" : (isMath ? "border-slate-100 hover:border-orange-200 focus-within:border-orange-300 focus-within:ring-4 focus-within:ring-orange-50" : "border-slate-100 hover:border-emerald-200 focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-50");

  return (
    <div className="p-4 md:p-8 flex-1 flex flex-col gap-5 fade-in relative pb-16 max-w-3xl mx-auto w-full">
      {loading && <ProcessingModal type={loadingType} />}
      
      <SmartNav 
          onNav={(action) => action === 'upload' ? null : alert("請先輸入題目並點擊「開始智能解題」")} 
          activeTab="upload" 
          userData={userData}
          plansMap={plansMap}
          sysConfig={sysConfig}
      />      

      {showFullSubjectTabs && (
          <div className="flex justify-center -mb-6 relative z-10">
              <div className="flex bg-slate-100/80 backdrop-blur-sm p-1.5 rounded-2xl shadow-inner border border-slate-200 w-full max-w-[280px]">
                  <button onClick={() => setSubject("math")} className={`flex-1 py-2.5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 ${isMath ? activeColorClass : inactiveColorClass}`}>
                      <div className={`w-2 h-2 rounded-full ${isMath ? "bg-orange-500 animate-pulse" : "bg-slate-300"}`}></div>
                      數理教室
                  </button>
                  <button onClick={() => setSubject("full")} className={`flex-1 py-2.5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 ${!isMath ? activeColorClass : inactiveColorClass}`}>
                      <div className={`w-2 h-2 rounded-full ${!isMath ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}></div>
                      語文教室
                      {isDiamond && !sysConfig?.enableFullSubjects && <Crown size={12} className="text-amber-500 ml-1" />}
                  </button>
              </div>
          </div>
      )}

      <div 
        className={`relative w-full bg-white rounded-[2rem] border-2 shadow-xl shadow-slate-200/50 transition-all duration-500 overflow-hidden group flex flex-col ${showFullSubjectTabs ? 'pt-6' : ''} ${borderColorClass}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }} 
        onDragLeave={() => { setIsDragging(false) }} 
        onDrop={handleDrop} 
        onPaste={handlePaste}
      >
        {selectedImage && (
            <div className="relative w-full h-48 bg-slate-50/50 flex items-center justify-center border-b border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                <img src={selectedImage} alt="preview" className="w-full h-full object-contain p-4" />
                <button onClick={(e) => { e.stopPropagation(); setSelectedImage(null); setImageBase64(null); }} className="absolute top-3 right-3 p-2 bg-black/40 hover:bg-red-500 text-white rounded-full transition-all backdrop-blur-sm shadow-sm z-10" title="移除圖片">
                    <X size={16} />
                </button>
            </div>
        )}

        <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={isMath ? "【數理教室】\n在此輸入數學、物理、化學題目...\n(支援 LaTeX 公式，可直接貼上圖片)" : "【語文教室】\n在此輸入英文翻譯、國文賞析、歷史事件...\n(請描述清楚問題背景)"}
            className={`w-full p-6 bg-transparent outline-none text-lg text-slate-700 placeholder:text-slate-300 resize-none leading-relaxed transition-all duration-300 ${selectedImage ? 'h-32' : 'h-48'}`}
        />

        <div className="flex items-center justify-between px-5 py-4 bg-slate-50/50 border-t border-slate-100 backdrop-blur-sm">
            <div 
                onClick={() => fileInputRef.current?.click()}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer transition-all duration-300 font-bold text-sm ${isMath ? "text-slate-500 hover:text-orange-600 hover:bg-orange-50" : "text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"}`}
            >
                <ImageIcon size={20} className={isMath ? "group-hover:text-orange-500" : "group-hover:text-emerald-500"}/>
                <span>上傳圖片</span>
                <input type="file" hidden ref={fileInputRef} onChange={(e) => processFile(e.target.files[0])} accept="image/*" />
            </div>

            <button 
                onClick={handleSolve} 
                disabled={loading || (!inputText && !selectedImage)}
                className={`px-8 py-3 rounded-xl font-black text-sm flex items-center gap-2 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg active:scale-95 duration-300 ${isMath ? "bg-gradient-to-r from-slate-800 to-slate-900 hover:from-orange-600 hover:to-orange-500 shadow-orange-200" : "bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 shadow-emerald-200"}`}
            >
                {loading ? <Loader2 className="animate-spin" size={18}/> : <Sparkles size={18} className="text-yellow-300"/>}
                {isMath ? '開始解題' : '開始分析'}
            </button>
        </div>

        {isDragging && (
            <div className={`absolute inset-0 backdrop-blur-sm border-4 border-dashed rounded-[2rem] flex flex-col items-center justify-center z-50 transition-colors ${isMath ? 'bg-orange-50/90 border-orange-400' : 'bg-emerald-50/90 border-emerald-400'}`}>
                <div className={`p-6 rounded-full shadow-2xl animate-bounce mb-4 ${isMath ? 'bg-orange-500 text-white' : 'bg-emerald-500 text-white'}`}>
                    <Upload size={40} />
                </div>
                <p className={`font-black text-lg ${isMath ? 'text-orange-600' : 'text-emerald-600'}`}>放開滑鼠以上傳</p>
            </div>
        )}
      </div>

      <CopyrightFooter />
    </div>
  );
};

export default InputPage;
